/**
 * Requisition housekeeping. 30-09-2026, migration 0188.
 *
 * Asked for by Procurement: raising a purchase order meant scrolling past
 * requisitions that were brand new, part-ordered, or simply no longer wanted
 * because the work at site had changed. This screen is where they tidy them,
 * site by site:
 *
 *   * CANCEL a requisition nothing has been ordered or transferred against
 *   * CLOSE any part of what is still open - per line, all or part of the
 *     quantity - as "no longer required". When nothing is left open, the
 *     requisition becomes CLOSED (short-closed).
 *
 * Either way it leaves the PO screen's requisition list. What the site asked
 * for is never changed; the database records what was declared not required
 * (pr_line_items.quantity_closed), who did it, when and why, and tells the
 * raiser and the site's Project Manager.
 *
 * The screen only proposes. rpc_pr_cancel and rpc_pr_close_lines check
 * everything again - the role, the status, a purchase order still in
 * approval, and every quantity.
 */
import { Fragment, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Brush, ChevronDown, ChevronRight, CircleSlash, PackageX } from 'lucide-react'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Modal, SearchInput, Select, StatusPill, Textarea, useToast,
} from '@/components/ui'
import { NotPermitted } from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { useMaterials, useQuery, useSiteNames, useSites } from '@/lib/data'
import { can, round3 } from '@/lib/domain'
import { ddmmyyyy, qty, rupees } from '@/lib/format'
import { supabase } from '@/lib/supabase'

interface Line {
  id: string; sl_no: number; material_id: string; uom: string | null
  quantity_requested: number; quantity_on_po: number; quantity_on_ist: number
  quantity_closed: number; estimated_rate: number
}
interface Pr {
  id: string; site_id: string; status: string; urgency: string | null
  date_created: string; approval_date: string | null; remarks: string | null
  pr_line_items: Line[]
}

// To 3 decimals, as the database counts it (v_open_pr_lines: open above 0.001).
// Rounding to 2 hid a left-over of 0.002 here while the PO screen still listed
// the requisition - so it could never be closed (05-10-2026).
const openOf = (l: Line) =>
  round3(Number(l.quantity_requested) - Number(l.quantity_on_po) - Number(l.quantity_on_ist) - Number(l.quantity_closed ?? 0))
const daysSince = (d?: string | null) =>
  d ? Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000)) : null

export function PrHousekeeping() {
  const { profile } = useAuth()
  const role = profile!.role
  const [params] = useSearchParams()
  const [site, setSite] = useState('')
  const [term, setTerm] = useState(params.get('pr') ?? '')
  const [open, setOpen] = useState<string | null>(params.get('pr'))
  const { siteName } = useSiteNames()
  const { data: sites } = useSites()

  const { data: prs, loading, error, refresh } = useQuery<Pr>('pr_housekeeping',
    () => supabase.from('purchase_requisitions')
      .select('id, site_id, status, urgency, date_created, approval_date, remarks, pr_line_items(id, sl_no, material_id, uom, quantity_requested, quantity_on_po, quantity_on_ist, quantity_closed, estimated_rate)')
      .eq('pr_type', 'STANDARD').in('status', ['APPROVED', 'PARTIALLY_FULFILLED'])
      .order('date_created', { ascending: true }),
    [], { cacheable: false })

  // Orders still in draft or approval: while one exists the database refuses
  // to touch the requisition, so say so here rather than after a click.
  const { data: pendingPos, refresh: refreshPos } = useQuery<any>('pr_housekeeping_pending_pos',
    () => supabase.from('purchase_orders').select('id, pr_id, status')
      .in('status', ['DRAFT', 'SUBMITTED', 'REVIEWED', 'RETURNED']).not('pr_id', 'is', null),
    [], { cacheable: false })
  const pendingFor = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const p of pendingPos) m.set(p.pr_id, [...(m.get(p.pr_id) ?? []), p.id])
    return m
  }, [pendingPos])

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    return prs
      .map((p) => ({ ...p, openLines: p.pr_line_items.filter((l) => openOf(l) > 0.001) }))
      .filter((p) => p.openLines.length > 0)
      .filter((p) => !site || p.site_id === site)
      .filter((p) => !t || p.id.toLowerCase().includes(t) || siteName(p.site_id).toLowerCase().includes(t))
  }, [prs, site, term, siteName])

  // Procurement acts; the CMD and Project Head, who see every screen, may look.
  const mayAct = can(role, 'prHousekeeping')
  if (!mayAct && role !== 'cmd' && role !== 'project_head') {
    return <NotPermitted what="tidy requisitions" who="the Procurement Executives, the Procurement Manager and the administrator" />
  }

  const reload = () => { refresh(); refreshPos() }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900">
          <Brush className="h-5 w-5 text-oval-700" /> Requisition Housekeeping
        </h1>
        <p className="text-sm text-slate-500">
          Approved and part-ordered requisitions, oldest first. Cancel one nothing has been ordered
          against, or close what is no longer required — it then leaves the purchase-order screen.
          The site is told, and the reason is kept.
        </p>
      </div>

      <Card>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm} placeholder="Requisition number or site…" />
          <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                  options={sites.map((s: any) => ({ value: s.id, label: s.site_name }))} />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0" title={`Open requisitions (${rows.length})`}>
        {loading && <Loading />}
        {error && <div className="p-4"><Alert tone="error">{error}</Alert></div>}
        {!loading && rows.length === 0 && (
          <EmptyState icon={<Brush className="h-8 w-8" />} title="Nothing to tidy"
                      message="No approved or part-ordered requisition has anything left open." />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <th /><th>Requisition</th><th>Site</th><th>Raised</th><th className="num">Age</th>
                  <th>Status</th><th className="num">Open lines</th><th className="num">Open value (est.)</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const isOpen = open === p.id
                  const value = p.openLines.reduce((a, l) => a + openOf(l) * Number(l.estimated_rate || 0), 0)
                  const age = daysSince(p.approval_date ?? p.date_created)
                  const blocked = pendingFor.get(p.id)
                  return (
                    <Fragment key={p.id}>
                      <tr className={isOpen ? 'bg-oval-50' : 'cursor-pointer hover:bg-slate-50'}
                          onClick={() => setOpen(isOpen ? null : p.id)}>
                        <td className="w-6 text-slate-400">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                        <td className="font-mono text-xs">
                          <Link to={`/prs/${p.id}`} className="text-oval-700 hover:underline" onClick={(e) => e.stopPropagation()}>{p.id}</Link>
                        </td>
                        <td className="text-sm">{siteName(p.site_id)}</td>
                        <td className="text-xs">{ddmmyyyy(p.date_created)}</td>
                        <td className={`num text-xs ${age != null && age > 60 ? 'font-semibold text-amber-700' : 'text-slate-500'}`}>{age != null ? `${age}d` : '—'}</td>
                        <td><StatusPill status={p.status} /></td>
                        <td className="num text-sm">{p.openLines.length} / {p.pr_line_items.length}</td>
                        <td className="num text-xs">{value ? rupees(value) : '—'}</td>
                        <td className="text-xs">{blocked && <span className="pill-amber" title={`Purchase order(s) ${blocked.join(', ')} still in draft or approval`}>PO in approval</span>}</td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={9} className="bg-slate-50/60 p-0">
                            {mayAct
                              ? <TidyPanel pr={p} blockedBy={blocked} onDone={() => { setOpen(null); reload() }} />
                              : <p className="p-4 text-sm text-slate-500">Read only. Procurement cancels or closes requisitions here.</p>}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
function TidyPanel({ pr, blockedBy, onDone }: {
  pr: Pr & { openLines: Line[] }; blockedBy?: string[]; onDone: () => void
}) {
  const toast = useToast()
  const { data: materials } = useMaterials()
  const byId = useMemo(() => Object.fromEntries(materials.map((m) => [m.id, m])), [materials])
  const [close, setClose] = useState<Record<string, string>>({})
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)

  const nothingOrdered = pr.pr_line_items.every((l) => Number(l.quantity_on_po) <= 0.0005 && Number(l.quantity_on_ist) <= 0.0005)
  const chosen = pr.openLines
    .map((l) => ({ l, q: Number(close[l.id] || 0) }))
    .filter((c) => c.q > 0)
  const over = chosen.find((c) => c.q > openOf(c.l) + 0.0005)
  const closesAll = pr.openLines.every((l) => Math.abs(Number(close[l.id] || 0) - openOf(l)) < 0.0005)
  const reasonOk = reason.trim().length >= 10

  async function doClose() {
    setBusy(true)
    const { data, error } = await supabase.rpc('rpc_pr_close_lines', {
      p_pr_id: pr.id, p_reason: reason.trim(),
      p_lines: chosen.map((c) => ({ line_id: c.l.id, qty: c.q })),
    })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success(data === 'CLOSED'
      ? `${pr.id} is closed — nothing is left open. The site has been told.`
      : `Marked no longer required. ${pr.id} stays open for the rest.`)
    onDone()
  }

  async function doCancel() {
    setBusy(true)
    const { error } = await supabase.rpc('rpc_pr_cancel', { p_pr_id: pr.id, p_reason: reason.trim() })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success(`${pr.id} cancelled. The site has been told.`)
    setConfirmCancel(false)
    onDone()
  }

  return (
    <div className="space-y-3 border-l-4 border-l-oval-700 p-4">
      {blockedBy && (
        <Alert tone="warning" title="A purchase order against this requisition is still in draft or approval">
          {blockedBy.join(', ')}. Finish, reject or delete it first — otherwise its quantity would land on
          a line you are closing. Nothing can be changed here until then.
        </Alert>
      )}
      {pr.remarks && <p className="text-xs text-slate-500">Site's note: {pr.remarks}</p>}

      <div className="table-wrap">
        <table className="tbl bg-white">
          <thead>
            <tr>
              <th>#</th><th>Material</th><th className="num">Requested</th><th className="num">On a PO</th>
              <th className="num">Via IST</th><th className="num">Not required</th><th className="num">Still open</th>
              <th className="num">No longer required now</th>
            </tr>
          </thead>
          <tbody>
            {pr.pr_line_items.slice().sort((a, b) => a.sl_no - b.sl_no).map((l) => {
              const o = openOf(l)
              const m = byId[l.material_id]
              return (
                <tr key={l.id} className={o <= 0.001 ? 'text-slate-400' : ''}>
                  <td className="text-xs text-slate-400">{l.sl_no}</td>
                  <td className="text-sm font-medium">{m?.item_name ?? l.material_id}</td>
                  <td className="num">{qty(l.quantity_requested)} <span className="text-xs text-slate-400">{l.uom}</span></td>
                  <td className="num text-xs">{qty(l.quantity_on_po)}</td>
                  <td className="num text-xs">{qty(l.quantity_on_ist)}</td>
                  <td className="num text-xs">{Number(l.quantity_closed) > 0.0005 ? qty(l.quantity_closed) : '—'}</td>
                  <td className="num font-semibold">{qty(o)}</td>
                  <td className="num">
                    {o > 0.001 ? (
                      <span className="flex items-center justify-end gap-1.5">
                        <Input type="number" min={0} max={o} step="any" className="w-28 text-right"
                               value={close[l.id] ?? ''} disabled={!!blockedBy}
                               onChange={(e) => setClose({ ...close, [l.id]: e.target.value })} />
                        <button type="button" className="btn-ghost btn-sm" disabled={!!blockedBy}
                                onClick={() => setClose({ ...close, [l.id]: String(o) })}>All</button>
                      </span>
                    ) : <span className="text-xs">done</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-ghost btn-sm" disabled={!!blockedBy}
                onClick={() => setClose(Object.fromEntries(pr.openLines.map((l) => [l.id, String(openOf(l))])))}>
          Mark everything still open as not required
        </button>
        {Object.keys(close).length > 0 && (
          <button type="button" className="btn-ghost btn-sm" onClick={() => setClose({})}>Clear</button>
        )}
      </div>

      <Field label="Why?" required hint="At least a short sentence. The raiser and the site's Project Manager are told, and it is kept permanently.">
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} disabled={!!blockedBy}
                  placeholder="e.g. Slab design changed on 25-09-2026; the extra shuttering is not needed." />
      </Field>

      {over && (
        <Alert tone="error">Line {over.l.sl_no}: only {qty(openOf(over.l))} is still open.</Alert>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          {chosen.length === 0 ? 'Enter how much of each line is no longer required.'
            : closesAll ? 'This closes everything still open — the requisition becomes Closed.'
              : `${chosen.length} line(s) will be reduced; the requisition stays open for the rest.`}
        </p>
        <div className="flex flex-wrap gap-2">
          {nothingOrdered && (
            <button className="btn-danger btn-sm" disabled={busy || !!blockedBy || !reasonOk}
                    onClick={() => setConfirmCancel(true)}
                    title="Nothing has been ordered or transferred against it, so it can be cancelled outright">
              <CircleSlash className="h-4 w-4" /> Cancel the requisition
            </button>
          )}
          <button className="btn-primary btn-sm" disabled={busy || !!blockedBy || !reasonOk || chosen.length === 0 || !!over}
                  onClick={() => void doClose()}>
            <PackageX className="h-4 w-4" /> {busy ? 'Saving…' : closesAll ? 'Close the requisition' : 'Mark as no longer required'}
          </button>
        </div>
      </div>

      {confirmCancel && (
        <Modal open onClose={() => setConfirmCancel(false)} title={`Cancel ${pr.id}?`}
               subtitle="Nothing has been ordered or transferred against it"
               footer={<>
                 <button className="btn-secondary" onClick={() => setConfirmCancel(false)} disabled={busy}>Keep it</button>
                 <button className="btn-danger" onClick={() => void doCancel()} disabled={busy}>
                   {busy ? 'Cancelling…' : 'Yes, cancel it'}
                 </button>
               </>}>
          <p className="text-sm">
            The whole requisition is cancelled and leaves the purchase-order screen. The raiser and the
            site's Project Manager are told why. <strong>This cannot be undone</strong> — if the material
            is needed after all, the site raises a new requisition.
          </p>
          <p className="mt-2 rounded-lg bg-slate-50 p-2 text-sm text-slate-700">“{reason.trim()}”</p>
        </Modal>
      )}
    </div>
  )
}
